import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import isEmail from 'validator/lib/isEmail';
import axios from 'axios';
import { FormField, ResultDialog, SubmitButton } from '../../components/ui/ui';
import { profile } from '../../data/portfolio';
import './FormikContact.scss';

const FORM_ENDPOINT = 'https://formspree.io/f/mrgdzgnr';
const topics = ['AI & Automation', 'Telecom & VoIP', 'Software', 'Data', 'Support', 'Something else'];

const contactItems = [
  { icon: 'fa-phone', label: 'Phone', value: profile.phone, href: `tel:${profile.phone.replace(/\s/g, '')}` },
  { icon: 'fa-envelope', label: 'Email', value: 'info@redemptioninc.co.zw', href: 'mailto:info@redemptioninc.co.zw' },
  { icon: 'fa-location-dot', label: 'Location', value: 'Pretoria, South Africa' }
];

const draftFor = (topic) => `Hi, I'd like to discuss ${topic}. Here's a bit about what we need:\n\n`;

// Pre-fills the message when a service card's "Discuss this" is clicked, unless the visitor already typed their own.
const TopicSync = ({ topic, lastDraft }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (!topic) return;
    if (!values.message || values.message === lastDraft.current) {
      const draft = draftFor(topic);
      lastDraft.current = draft;
      setFieldValue('message', draft);
    }
    setFieldValue('subject', topic);
    // Only react to a new topic, not to every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  return null;
};

const Contact = ({ topic = '' }) => {
  const [status, setStatus] = useState('idle');
  const [dialog, setDialog] = useState(null);
  const lastDraft = useRef('');

  const initialValues = { name: '', email: '', subject: '', message: '' };

  const validate = (values) => {
    const errors = {};
    if (!values.name.trim()) errors.name = 'Please tell me your name';
    if (!values.email) errors.email = 'An email lets me reply';
    else if (!isEmail(values.email)) errors.email = "That email doesn't look right";
    if (values.message.trim().length < 20) errors.message = 'A little more detail helps (20+ characters)';
    return errors;
  };

  const onSubmit = async (values, { resetForm }) => {
    setStatus('loading');
    try {
      const response = await axios.post(FORM_ENDPOINT, {
        name: values.name,
        email: values.email,
        subject: values.subject ? `Enquiry: ${values.subject}` : 'Website enquiry',
        topic: values.subject,
        message: values.message
      });
      if (response.status !== 200) throw new Error('Unexpected response');
      setStatus('success');
      setDialog({ type: 'success', title: 'Message sent!', message: 'Thanks for reaching out. You’ll hear back soon at the email you provided.' });
      resetForm();
      lastDraft.current = '';
      setTimeout(() => setStatus('idle'), 2600);
    } catch (error) {
      setStatus('error');
      setDialog({ type: 'error', title: 'Message not sent', message: 'Something went wrong on the way. Please try again, or email info@redemptioninc.co.zw directly.' });
    }
  };

  return (
    <section id="contact" className="cx-section">
      <div className="ui-container cx-layout">
        <div className="cx-info" data-reveal>
          <p className="ui-eyebrow">Get in touch</p>
          <h2>Let’s build something <span className="ui-gradient-text">that works</span>.</h2>
          <p className="cx-lead">Tell me about the project, the problem or the idea. You’ll get a thoughtful reply, not an automated sales pitch.</p>

          <ul className="cx-items">
            {contactItems.map((item, index) => (
              <li key={item.label} style={{ '--i': index }}>
                <span className="cx-items__icon"><i className={`fas ${item.icon}`} /></span>
                <span>
                  <small>{item.label}</small>
                  {item.href ? <a href={item.href}>{item.value}</a> : <strong>{item.value}</strong>}
                </span>
              </li>
            ))}
          </ul>

          <div className="cx-social">
            <a href={profile.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp"><i className="fa-brands fa-whatsapp" /></a>
            <a href="https://github.com/PRedemption" target="_blank" rel="noreferrer" aria-label="GitHub"><i className="fa-brands fa-github" /></a>
            <a href="https://linkedin.com/in/paradzai-gwira-216169194" target="_blank" rel="noreferrer" aria-label="LinkedIn"><i className="fa-brands fa-linkedin-in" /></a>
          </div>
        </div>

        <div className="ui-card cx-card" data-reveal style={{ '--delay': '120ms' }}>
          <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnMount={false}>
            {(formik) => (
              <Form noValidate>
                <TopicSync topic={topic} lastDraft={lastDraft} />

                <p className="cx-label">What’s it about?</p>
                <div className="cx-topics" role="radiogroup" aria-label="Topic">
                  {(topic && !topics.includes(topic) ? [topic, ...topics] : topics).map((item) => (
                    <button
                      key={item}
                      type="button"
                      role="radio"
                      aria-checked={formik.values.subject === item}
                      className={`cx-topic ${formik.values.subject === item ? 'is-active' : ''}`}
                      onClick={() => formik.setFieldValue('subject', formik.values.subject === item ? '' : item)}
                    >
                      {formik.values.subject === item && <i className="fas fa-check" />} {item}
                    </button>
                  ))}
                </div>

                <div className="ui-form-grid">
                  <FormField name="name" label="Full name" autoComplete="name" required />
                  <FormField name="email" label="Email address" type="email" autoComplete="email" required />
                  <FormField className="span-2" name="message" label="Your message" as="textarea" rows="6" maxLength={2000} required hint="Goals, timelines and budget all help." />
                </div>

                <div className="cx-submit">
                  <SubmitButton status={status} labels={{ idle: 'Send message', loading: 'Sending…', success: 'Sent!', error: 'Try again' }} />
                  <span className="cx-note"><i className="fas fa-lock" /> Your details are only used to reply.</span>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <ResultDialog open={Boolean(dialog)} {...(dialog || {})} onClose={() => { setDialog(null); if (status === 'error') setStatus('idle'); }} />
    </section>
  );
};

export default Contact;
